import { useState } from 'react';
import { MdApartment, MdWork, MdLocationOn } from 'react-icons/md';
import { Button } from '@/components/Button';
import MoneyBagIcon from '@/components/icons/MoneyBagIcon';
import { OverflowTooltip } from '@/components/ui/overflow-tooltip';
import type { CandidateEndpointResponse, JobPostEndpoint } from '../types/CandidateEndpoint';
import CandidatesOfferCommentModal from './CandidatesOfferCommentModal';
import CandidatesStatusChangeModal from './CandidatesStatusChangeModal';
import type { StatusChangeType } from './CandidatesStatusChangeModal';
import type { RefetchOptions, QueryObserverResult } from '@tanstack/react-query';
import { Tag } from '@/components/Tag';
import Checkbox from '@/components/Checkbox';

type Props = {
  jobPostEndpoint: JobPostEndpoint;
  candidateProfileId: number;
  candidateEndpointRefetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<CandidateEndpointResponse, Error>>;
  isChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  selectedStatusGroup?: 'actionable' | 'resettable' | null;
};

export default function CandidatesOfferListItem({
  jobPostEndpoint,
  candidateProfileId,
  candidateEndpointRefetch,
  isChecked = false,
  onCheckedChange,
  selectedStatusGroup = null,
}: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusChangeType, setStatusChangeType] = useState<StatusChangeType>('toSuggest');
  const [comment, setComment] = useState(jobPostEndpoint.comment ?? '');
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);

  const openModal = (type: StatusChangeType) => {
    setStatusChangeType(type);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const isCheckboxDisabled = (() => {
    const status = jobPostEndpoint.status;

    if (status === 'matched') return true;

    // 選択済みは常に解除可能にする（絞り込みを跨いだ混在選択で解除不能になるのを防ぐ）
    if (isChecked) return false;

    if (!selectedStatusGroup) return false;

    if (selectedStatusGroup === 'actionable') {
      return status !== 'suggest' && status !== 'apply';
    }

    if (selectedStatusGroup === 'resettable') {
      return status !== 'decline' && status !== 'archived';
    }

    return false;
  })();

  return (
    <div className="relative flex rounded-sm bg-white px-xxl py-7 shadow-layer2">
      {/* カードの内容 */}
      <div className="flex min-w-0 flex-1 flex-col gap-5">
        <div className="flex flex-col gap-sm">
          <div className="flex items-center justify-between gap-xs">
            <div className="flex min-w-0 items-center gap-xs">
              <div
                className="flex shrink-0 items-center"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Checkbox
                  checked={isChecked}
                  disabled={isCheckboxDisabled}
                  onChange={(checked) => {
                    onCheckedChange?.(checked);
                  }}
                />
              </div>
              <p className="line-clamp-1 text-heading-sm">
                {jobPostEndpoint.provided_job_post.title}
              </p>
            </div>
            <Tag
              size="lg"
              className="shrink-0"
              color={
                (
                  {
                    suggest: 'orange',
                    apply: 'green',
                    decline: 'gray',
                    archived: 'red',
                    matched: 'blue',
                  } as const
                )[jobPostEndpoint.status]
              }
            >
              {(() => {
                switch (jobPostEndpoint.status) {
                  case 'suggest':
                    return '提案中';
                  case 'apply':
                    return '応募中';
                  case 'decline':
                    return '辞退';
                  case 'archived':
                    return '取り下げ';
                  case 'matched':
                    return 'マッチ済み';
                  default:
                    return jobPostEndpoint.status;
                }
              })()}
            </Tag>
          </div>
          <div className="flex min-w-0 items-center gap-5">
            <div className="flex min-w-0 shrink-0 items-center gap-xs">
              <MdApartment className="size-4 shrink-0 text-sub" aria-hidden="true" />
              <OverflowTooltip
                text={jobPostEndpoint.provided_job_post.company.name}
                className="text-body-xs"
              />
            </div>
            {jobPostEndpoint.provided_job_post.job_position_name && (
              <div className="flex min-w-0 items-center gap-xs">
                <MdWork className="size-4 shrink-0 text-sub" aria-hidden="true" />
                <OverflowTooltip
                  text={jobPostEndpoint.provided_job_post.job_position_name}
                  className="text-body-xs"
                />
              </div>
            )}
            {jobPostEndpoint.provided_job_post.salary_info && (
              <div className="flex min-w-0 shrink-0 items-center gap-xs">
                <MoneyBagIcon className="size-4 shrink-0 text-sub" aria-hidden="true" />
                <OverflowTooltip
                  text={jobPostEndpoint.provided_job_post.salary_info}
                  className="text-body-xs"
                />
              </div>
            )}
            {jobPostEndpoint.provided_job_post.working_location && (
              <div className="flex min-w-0 items-center gap-xs">
                <MdLocationOn className="size-4 shrink-0 text-sub" aria-hidden="true" />
                <OverflowTooltip
                  text={jobPostEndpoint.provided_job_post.working_location}
                  className="text-body-xs"
                />
              </div>
            )}
          </div>
          <div className="flex flex-col gap-xs">
            <div className="flex gap-xs">
              <p className="text-heading-xxs">エージェントからのコメント</p>
            </div>
            <div>
              <div
                role="button"
                tabIndex={0}
                className="h-x4l cursor-pointer overflow-auto rounded-sm border border-solid border-border p-xs text-body-xs break-words whitespace-pre-wrap"
                onClick={() => setIsCommentModalOpen(true)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setIsCommentModalOpen(true);
                  }
                }}
              >
                {comment}
              </div>
            </div>
          </div>
        </div>
        <div className="flex">
          <Button type="button" color="secondary" size="md" asChild className="w-[120px]">
            <a
              href={jobPostEndpoint.provided_job_post.urls.show}
              target="_blank"
              rel="noopener noreferrer"
            >
              求人詳細を見る
            </a>
          </Button>
          <div className="ml-auto flex gap-sm">
            {(jobPostEndpoint.status === 'suggest' || jobPostEndpoint.status === 'apply') && (
              <Button
                type="button"
                color="danger-secondary"
                size="md"
                onClick={() => openModal('toWithdraw')}
                className="w-[120px]"
              >
                取り下げ
              </Button>
            )}

            {(jobPostEndpoint.status === 'decline' || jobPostEndpoint.status === 'archived') && (
              <Button
                type="button"
                color="secondary"
                size="md"
                onClick={() => openModal('toSuggest')}
                className="w-[120px]"
              >
                提案へ戻す
              </Button>
            )}

            {/* 取り下げ済みのみ物理削除可能 */}
            {jobPostEndpoint.status === 'archived' && (
              <Button
                type="button"
                color="danger-secondary"
                size="md"
                onClick={() => openModal('toDelete')}
                className="w-[120px]"
              >
                削除
              </Button>
            )}

            {(jobPostEndpoint.status === 'suggest' || jobPostEndpoint.status === 'apply') && (
              <Button
                type="button"
                color="primary"
                size="md"
                onClick={() => openModal('toSelection')}
                className="w-[120px]"
              >
                選考へ進む
              </Button>
            )}
          </div>
        </div>

        <CandidatesOfferCommentModal
          modal={{
            id: `candidates-offer-comment-modal-${jobPostEndpoint.id}`,
            isShow: isCommentModalOpen,
          }}
          close={() => setIsCommentModalOpen(false)}
          title={jobPostEndpoint.provided_job_post.title}
          initialComment={comment}
          candidateProfileId={candidateProfileId}
          jobPostEndpointId={jobPostEndpoint.id}
          onSaved={setComment}
        />

        <CandidatesStatusChangeModal
          modal={{
            id: `candidates-status-change-modal-${jobPostEndpoint.id}`,
            isShow: isModalOpen,
          }}
          close={closeModal}
          statusChangeType={statusChangeType}
          candidateProfileId={candidateProfileId}
          ids={[jobPostEndpoint.id]}
          candidateEndpointRefetch={candidateEndpointRefetch}
        />
      </div>
    </div>
  );
}
